import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';
import JsonLd, { SITE_URL, SITE_NAME, SITE_LOGO } from '@/components/feature/JsonLd';

export default function ArticlePage() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const toc = [
    { id: 'what', label: t('whatTitle') },
    { id: 'how', label: t('howTitle') },
    { id: 'categories', label: t('categoriesTitle') },
    { id: 'qc', label: t('qcTitle') },
    { id: 'shipping', label: t('shippingTitle') },
    { id: 'faq', label: t('faqTitle') },
  ];

  const takeaways = [t('takeaway1'), t('takeaway2'), t('takeaway3'), t('takeaway4'), t('takeaway5')];

  const tableRows = [
    [t('tableRow1Col1'), t('tableRow1Col2'), t('tableRow1Col3')],
    [t('tableRow2Col1'), t('tableRow2Col2'), t('tableRow2Col3')],
    [t('tableRow3Col1'), t('tableRow3Col2'), t('tableRow3Col3')],
    [t('tableRow4Col1'), t('tableRow4Col2'), t('tableRow4Col3')],
    [t('tableRow5Col1'), t('tableRow5Col2'), t('tableRow5Col3')],
  ];

  const faqs = [
    { q: t('faq1q'), a: t('faq1a') },
    { q: t('faq2q'), a: t('faq2a') },
    { q: t('faq3q'), a: t('faq3a') },
    { q: t('faq4q'), a: t('faq4a') },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t('title'),
    description: t('metaDescription'),
    author: { '@type': 'Organization', name: t('authorName') },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: SITE_LOGO },
    },
    datePublished: '2026-08-01',
    dateModified: '2026-08-20',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/usfans-spreadsheet-guide`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={faqSchema} />
      <Navbar />

      <main className="pt-24">
        <article className="mx-auto max-w-3xl px-4 md:px-6">
          <nav aria-label="Breadcrumb" className="text-sm text-foreground-500">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="text-primary-700 hover:text-primary-800">
                  {t('breadcrumbHome')}
                </Link>
              </li>
              <li className="text-foreground-300">/</li>
              <li className="text-foreground-600">{t('breadcrumbCurrent')}</li>
            </ol>
          </nav>

          <header className="mt-6">
            <h1 className="text-3xl font-bold leading-tight text-foreground-950 md:text-4xl font-heading">{t('h1')}</h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-500">
              <span className="flex items-center gap-2">
                <i className="ri-user-line"></i>
                <span>{t('authorLabel')}</span>
                <strong className="font-semibold text-foreground-700">{t('authorName')}</strong>
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                <span>{t('publishedLabel')}: {t('publishedDate')}</span>
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-refresh-line"></i>
                <span>{t('updatedLabel')}: {t('updatedDate')}</span>
              </span>
            </div>
          </header>

          <div className="mt-8 space-y-5 text-base leading-relaxed text-foreground-700">
            <p>{t('intro1')}</p>
            <p>{t('intro2')}</p>
          </div>

          <div className="mt-8 rounded-lg border border-primary-200 bg-primary-50 p-6">
            <h2 className="text-lg font-bold text-foreground-950 font-heading">{t('takeawaysTitle')}</h2>
            <ul className="mt-4 space-y-2.5">
              {takeaways.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <nav className="mt-8 rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="text-sm font-semibold text-foreground-950">{t('tocTitle')}</h2>
            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {toc.map((item) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-700">
                    <i className="ri-arrow-right-s-line text-secondary-500"></i>
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <section id="what" className="mt-10 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground-950 font-heading">{t('whatTitle')}</h2>
            <div className="mt-4 space-y-4 text-base leading-relaxed text-foreground-700">
              <p>{t('what1')}</p>
              <p>{t('what2')}</p>
            </div>
          </section>

          <section id="how" className="mt-10 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground-950 font-heading">{t('howTitle')}</h2>
            <div className="mt-4 space-y-4 text-base leading-relaxed text-foreground-700">
              <p>{t('how1')}</p>
              <p>{t('how2')}</p>
            </div>
          </section>

          <section id="categories" className="mt-10 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground-950 font-heading">{t('categoriesTitle')}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('categories1')}</p>
          </section>

          <section id="qc" className="mt-10 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground-950 font-heading">{t('qcTitle')}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('qc1')}</p>
          </section>

          <section id="shipping" className="mt-10 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground-950 font-heading">{t('shippingTitle')}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('shipping1')}</p>
          </section>

          <section className="mt-10">
            <h2 className="text-2xl font-bold text-foreground-950 font-heading">{t('tableTitle')}</h2>
            <div className="mt-4 overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full min-w-[480px] text-left text-sm">
                <thead>
                  <tr className="bg-background-100 text-foreground-700">
                    <th className="px-4 py-3 font-semibold">{t('tableHeader1')}</th>
                    <th className="px-4 py-3 font-semibold">{t('tableHeader2')}</th>
                    <th className="px-4 py-3 font-semibold">{t('tableHeader3')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row) => (
                    <tr key={row[0]} className="border-t border-background-200">
                      <td className="px-4 py-3 font-semibold text-foreground-950">{row[0]}</td>
                      <td className="px-4 py-3 text-foreground-600">{row[1]}</td>
                      <td className="px-4 py-3 text-foreground-600">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section id="faq" className="mt-10 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground-950 font-heading">{t('faqTitle')}</h2>
            <div className="mt-4 space-y-3">
              {faqs.map((f, i) => (
                <div key={f.q} className="rounded-lg border border-background-200 bg-background-50">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left text-sm font-semibold text-foreground-900 cursor-pointer"
                  >
                    <span>{f.q}</span>
                    <i className={`ri-add-line text-lg text-secondary-500 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}></i>
                  </button>
                  {openFaq === i && (
                    <div className="border-t border-background-200 px-5 py-4 text-sm leading-relaxed text-foreground-600">
                      {f.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <div className="mt-10 rounded-lg bg-secondary-50 border border-secondary-200 p-8 text-center">
            <h2 className="text-xl font-bold text-foreground-950 font-heading">{t('ctaTitle')}</h2>
            <p className="mt-3 text-sm text-foreground-600">{t('ctaText')}</p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-base font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer sm:w-auto"
              >
                <i className="ri-leaf-line"></i>
                {t('usfansSpreadsheet')}
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-base font-semibold text-background-50 hover:bg-accent-600 whitespace-nowrap cursor-pointer sm:w-auto"
              >
                <i className="ri-shopping-cart-line"></i>
                {t('buyNow')}
              </a>
            </div>
          </div>

          <div className="mt-8 flex justify-center pb-16">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              {t('backHome')}
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}